import { ChangeEvent, useState } from "react";
import { Form } from "react-bootstrap";
import TextField from "@components/FormsV2/TextField/TextField";
import FormNumberFieldV2 from "@components/FormsV2/FormNumberFieldV2/FormNumberFieldV2";
import RequiredReactSelect from "@components/FormsV2/RequiredReactSelect/RequiredReactSelect";
import type { ResourceRateFormValues } from "../types";

export interface ResourceRateFormOption {
  value: string;
  label: string;
}

export interface ResourceRateFormProps {
  id: string;
  initialValues: ResourceRateFormValues;
  resourceOptions: ResourceRateFormOption[];
  lobOptions: ResourceRateFormOption[];
  isEditing: boolean;
  isSubmitting: boolean;
  onSubmit: (values: ResourceRateFormValues) => void;
}

export const ResourceRateForm = ({
  id,
  initialValues,
  resourceOptions,
  lobOptions,
  isEditing,
  isSubmitting,
  onSubmit,
}: ResourceRateFormProps): JSX.Element => {
  const [values, setValues] = useState<ResourceRateFormValues>(initialValues);

  const set = <K extends keyof ResourceRateFormValues>(
    key: K,
    v: ResourceRateFormValues[K]
  ) => setValues((prev) => ({ ...prev, [key]: v }));

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    onSubmit(values);
  };

  const numeric = (e: ChangeEvent<HTMLInputElement>) =>
    e.target.value === "" ? null : Number(e.target.value);

  const selectedResource = resourceOptions.find(
    (o) => o.value === values.resourceId
  );
  const selectedLob = lobOptions.find((o) => o.value === values.lobId);

  return (
    <Form id={id} data-testid={id} onSubmit={handleSubmit}>
      <div className="row">
        <div className="col-sm-3">
          <RequiredReactSelect
            label="Resource"
            value={selectedResource}
            onChange={(opt: any) => set("resourceId", opt?.value ?? "")}
            options={resourceOptions}
            placeholder="Select…"
            required
            isDisabled={isEditing}
          />
        </div>
        <div className="col-sm-3">
          <Form.Label htmlFor="rr-external-name">External Name</Form.Label>
          <TextField
            label={null}
            id="rr-external-name"
            value={values.externalName}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              set("externalName", e.target.value)
            }
          />
        </div>
        <div className="col-sm-3">
          <Form.Label htmlFor="rr-description">Description</Form.Label>
          <TextField
            label={null}
            id="rr-description"
            value={values.description}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              set("description", e.target.value)
            }
          />
        </div>
        <div className="col-sm-3">
          <RequiredReactSelect
            label="Line of Business"
            value={selectedLob}
            onChange={(opt: any) => set("lobId", opt?.value ?? "")}
            options={lobOptions}
            placeholder="All Lines of Business"
          />
        </div>
      </div>
      <div className="row mt-3">
        <div className="col-sm-3">
          <FormNumberFieldV2
            label="Price"
            value={values.hourlyRate}
            min={0}
            step={0.01}
            required
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              set("hourlyRate", numeric(e))
            }
          />
        </div>
        <div className="col-sm-3">
          <FormNumberFieldV2
            label="Cost"
            value={values.hourlyCost}
            min={0}
            step={0.01}
            required
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              set("hourlyCost", numeric(e))
            }
          />
        </div>
        <div className="col-sm-3">
          <FormNumberFieldV2
            label="Travel/Expense Rate"
            value={values.expenseRate}
            min={0}
            step={0.01}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              set("expenseRate", numeric(e))
            }
          />
        </div>
        <div className="col-sm-3">
          <Form.Label htmlFor="rr-code">Code</Form.Label>
          <TextField
            label={null}
            id="rr-code"
            value={values.code}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              set("code", e.target.value)
            }
          />
        </div>
      </div>
    </Form>
  );
};

export default ResourceRateForm;
